"use client";

import { memo, useCallback, useMemo } from "react";
import type { SoundEffectName } from "@thehoneyjar/sound-engine";
import { useSoundEffects } from "@thehoneyjar/sound-engine";
import { TVOverlay } from "@/components/tv-overlay";
import type { GameApp } from "@/constants/apps";
import {
  HIBERNATION_POKU_FILTER_LIVING,
  HIBERNATION_POKU_FILTER_STRONG,
  KANSEI_DEAD,
} from "@/constants/arcade-styles";
import Image from "next/image";

// Export the type for use in other components
export type ButtonCategory = Exclude<GameApp["category"], "buy">;

const SECTION_HEADER_STYLE = {
  // Hibernation chrome: dark moondust gradient + cobalt rim. Matches
  // the navbar / TV vocabulary so the sections read as the same room.
  background: "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)",
  boxShadow: `
    0 -2px 8px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    inset 0 -1px 0 rgba(0, 0, 0, 0.7),
    0 0 0 1px rgba(62, 97, 250, 0.14)
  `.trim(),
  borderTop: "1px solid rgba(62, 97, 250, 0.18)",
  borderLeft: "1px solid rgba(62, 97, 250, 0.14)",
  borderRight: "1px solid rgba(62, 97, 250, 0.14)",
};

const SECTION_BODY_STYLE = {
  background: "linear-gradient(145deg, #2a2a32 0%, #1f1f25 30%, #161620 70%, #0d0d12 100%)",
  boxShadow: `
    0 12px 40px rgba(0, 0, 0, 0.45),
    0 4px 12px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    inset 0 -1px 0 rgba(0, 0, 0, 0.7),
    0 0 0 1px rgba(62, 97, 250, 0.14)
  `.trim(),
  borderBottom: "1px solid rgba(62, 97, 250, 0.18)",
  borderLeft: "1px solid rgba(62, 97, 250, 0.14)",
  borderRight: "1px solid rgba(62, 97, 250, 0.14)",
  borderTop: "none",
  marginTop: "-1px",
};

interface ArcadeSectionProps {
  title: string;
  titleColor: string;
  apps: GameApp[];
  hoveredApp: string | null;
  onAppClick: (app: GameApp) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}

export const ArcadeSection = memo(({
  title,
  titleColor,
  apps,
  hoveredApp,
  onAppClick,
  onHoverEnter,
  onHoverLeave,
}: ArcadeSectionProps) => {
  // Determine button category based on section title
  const buttonCategory = title.toLowerCase() as ButtonCategory;

  return (
    <div>
      <div
        className="relative overflow-hidden rounded-t-md"
        style={SECTION_HEADER_STYLE}
      >
        <div className="px-3 py-1">
          {/* Section label — moondust zinc, flickers dying. Drop the
              chromatic titleColor (yellow / purple) for AFTER HOURS. */}
          <h2
            className="kansei-flicker-b font-pressStart2P text-[8px] uppercase tracking-[0.18em] text-zinc-500"
          >
            {title}
          </h2>
        </div>
      </div>
      <div
        className="relative rounded-b-md p-2"
        style={SECTION_BODY_STYLE}
      >
        <div className="">
          {apps.map((app, index) => (
            <ArcadeButton
              key={app.id}
              app={app}
              index={index}
              totalApps={apps.length}
              category={buttonCategory}
              isHovered={hoveredApp === app.id}
              onAppClick={onAppClick}
              onHoverEnter={onHoverEnter}
              onHoverLeave={onHoverLeave}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

ArcadeSection.displayName = "ArcadeSection";

// Static map — moved to module scope to avoid per-render allocation
const CLICK_SOUND_BY_CATEGORY: Partial<Record<ButtonCategory, SoundEffectName>> = {
  play: "buttonClickPlay",
  guts: "buttonClickGuts",
  create: "buttonClickCreate",
  earn: "buttonClickEarn",
};

interface ArcadeButtonProps {
  app: GameApp;
  index: number;
  totalApps: number;
  category: ButtonCategory;
  isHovered: boolean;
  onAppClick: (app: GameApp) => void;
  onHoverEnter: (id: string) => void;
  onHoverLeave: () => void;
}

export const ArcadeButton = memo(({
  app,
  index,
  totalApps,
  category,
  isHovered,
  onAppClick,
  onHoverEnter,
  onHoverLeave,
}: ArcadeButtonProps) => {
  const { playSound } = useSoundEffects();

  const clickSound = CLICK_SOUND_BY_CATEGORY[category] ?? "buttonClick";

  const handleClick = useCallback(() => {
    // Play category-specific click sound for unlocked apps
    if (!app.isLocked) {
      // TODO(audio-register): consider isRouteHibernating gate — see constants/soundtrack.ts
      void playSound(clickSound);
      onAppClick(app);
    }
  }, [app, clickSound, onAppClick, playSound]);

  const handleMouseEnter = useCallback(() => {
    // Only allow hover for unlocked apps
    if (!app.isLocked) {
      // TODO(audio-register): consider isRouteHibernating gate — see constants/soundtrack.ts
      void playSound("buttonHover");
      onHoverEnter(app.id);
    }
  }, [app.id, app.isLocked, onHoverEnter, playSound]);

  const handleMouseLeave = useCallback(() => {
    // Only trigger leave for unlocked apps
    if (!app.isLocked) {
      onHoverLeave();
    }
  }, [app.isLocked, onHoverLeave]);

  const buttonStyle = useMemo(
    () => ({
      // AFTER HOURS register: dark moondust background + cobalt rim
      // signal (replaces the prior black borders + white-on-hover —
      // those broke register coherence with the rest of the room).
      // Hover brightens the cobalt rim and adds a faint cobalt halo
      // for depth without lighting the card up.
      background: `linear-gradient(135deg, #0a0a0b 0%, #141416 100%)`,
      boxShadow: isHovered
        ? `
          0 1px 2px rgba(0, 0, 0, 0.9),
          0 2px 4px rgba(0, 0, 0, 0.8),
          0 4px 8px rgba(0, 0, 0, 0.6),
          inset 0 1px 1px rgba(255, 255, 255, 0.06),
          inset 0 2px 12px rgba(0, 0, 0, 0.9),
          0 0 12px rgba(62, 97, 250, 0.18)
        `.trim()
        : `
          0 1px 2px rgba(0, 0, 0, 0.8),
          0 2px 4px rgba(0, 0, 0, 0.7),
          inset 0 1px 1px rgba(255, 255, 255, 0.03),
          inset 0 2px 10px rgba(0, 0, 0, 0.8)
        `.trim(),
      borderLeft: `1px solid ${isHovered ? "rgba(62, 97, 250, 0.32)" : "rgba(62, 97, 250, 0.14)"}`,
      borderRight: `1px solid ${isHovered ? "rgba(62, 97, 250, 0.32)" : "rgba(62, 97, 250, 0.14)"}`,
      borderTop:
        index === 0
          ? `1px solid ${isHovered ? "rgba(62, 97, 250, 0.32)" : "rgba(62, 97, 250, 0.18)"}`
          : "none",
      borderBottom: `1px solid ${isHovered ? "rgba(62, 97, 250, 0.32)" : "rgba(62, 97, 250, 0.14)"}`,
      transform: isHovered ? "translateY(-1px)" : "translateY(0)",
      transition: `${KANSEI_DEAD.TRANSITION.BORDER_QUANTUM}, ${KANSEI_DEAD.TRANSITION.SHADOW_QUANTUM}, ${KANSEI_DEAD.TRANSITION.TRANSFORM_QUANTUM}`,
    }),
    [index, isHovered],
  );

  return (
    <button
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`group relative w-full overflow-hidden transition-all duration-200 ease-out ${
        index === 0 && index === totalApps - 1
          ? "rounded-md"
          : index === 0
          ? "rounded-t-md"
          : index === totalApps - 1
          ? "rounded-b-md"
          : ""
      } ${app.isLocked ? "cursor-default" : "cursor-pointer"}`}
      style={buttonStyle}
    >
      <div
        className={`relative h-20 overflow-hidden ${
          index === 0 && index === totalApps - 1
            ? "rounded-md"
            : index === 0
            ? "rounded-t-md"
            : index === totalApps - 1
            ? "rounded-b-md"
            : ""
        } transition-[transform,filter] duration-150 ease-out will-change-transform active:scale-[0.98]`}
        style={{
          transform: isHovered ? "scale(1.01)" : "scale(1)",
          filter: app.isLocked ? "grayscale(1)" : "none",
        }}
      >
        {/* App Image or Title Display - behind everything */}
        <AppDisplay app={app} isHovered={isHovered} />

        {/* Dimming overlay - creates subtle rest state darkness */}
        <div
          className="pointer-events-none absolute inset-0 z-[5] transition-opacity duration-300"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            opacity: isHovered ? 0 : 1,
          }}
        />

        {/* TV Overlay - on top of image */}
        <TVOverlay intensity={isHovered ? "light" : "medium"} />

        {/* Inset shadow overlay - on top of everything */}
        <div
          className="pointer-events-none absolute inset-0 z-10 transition-shadow duration-300"
          style={{
            boxShadow: isHovered
              ? `
                inset 0 1px 1px rgba(255, 255, 255, 0.08),
                inset 0 2px 12px rgba(0, 0, 0, 0.7),
                inset 0 -2px 10px rgba(0, 0, 0, 0.5),
                inset 0 0 20px rgba(0, 0, 0, 0.3)
              `.trim()
              : `
                inset 0 1px 1px rgba(255, 255, 255, 0.02),
                inset 0 2px 16px rgba(0, 0, 0, 0.95),
                inset 0 -2px 12px rgba(0, 0, 0, 0.85),
                inset 0 0 25px rgba(0, 0, 0, 0.7)
              `.trim(),
          }}
        />

        {/* APY Display - Compact corner badge */}
        {app.apy && (
          <div
            className="absolute bottom-1 right-1 z-30 transition-transform duration-200"
            style={{
              background: "rgba(10, 10, 11, 0.95)",
              borderRadius: "3px",
              padding: "3px 6px 2px 6px",
              boxShadow: `
                0 0 0 0.5px rgba(255, 255, 255, 0.1),
                0 1px 2px rgba(0, 0, 0, 0.9),
                0 2px 4px rgba(0, 0, 0, 0.8),
                0 4px 8px rgba(0, 0, 0, 0.7),
                0 8px 16px rgba(0, 0, 0, 0.6),
                inset 0 0.5px 0.5px rgba(255, 255, 255, 0.08),
                inset 0 -0.5px 0.5px rgba(0, 0, 0, 0.5)
              `.trim(),
              border: "0.5px solid rgba(0, 0, 0, 0.9)",
              backdropFilter: "blur(8px)",
              transform: isHovered ? "scale(1.05)" : "scale(1)",
            }}
          >
            {/* Subtle scanline overlay */}
            <div
              className="pointer-events-none absolute inset-0 opacity-20"
              style={{
                background: `repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 1px,
                  rgba(255, 255, 255, 0.02) 1px,
                  rgba(255, 255, 255, 0.02) 2px
                )`,
                borderRadius: "3px",
              }}
            />

            {/* Glow effect behind text */}
            <div
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                background: `radial-gradient(circle at 30% 30%, ${
                  app.id === "vault" ? "rgba(134, 239, 172, 0.15)" : "rgba(96, 165, 250, 0.15)"
                }, transparent 70%)`,
                borderRadius: "3px",
              }}
            />

            {/* APY content - single line */}
            <div className="relative z-10 flex items-baseline gap-1 leading-none">
              <span
                className="font-pressStart2P text-[7px]"
                style={{
                  color: app.id === "vault" ? "#86efac" : "#60a5fa",
                  textShadow: `
                    0 0 3px ${app.id === "vault" ? "rgba(134, 239, 172, 0.4)" : "rgba(96, 165, 250, 0.4)"},
                    0 1px 2px rgba(0, 0, 0, 0.9)
                  `.trim(),
                  letterSpacing: "0.2px",
                }}
              >
                {app.apy}
              </span>
              <span
                className="font-pressStart2P text-[7px]"
                style={{
                  color: "rgba(255, 255, 255, 1)",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.9), 0 0 4px rgba(255, 255, 255, 0.3)",
                  letterSpacing: "0.2px",
                  opacity: 0.9,
                }}
              >
                APY
              </span>
            </div>
          </div>
        )}

        {/* Locked overlay — operator (session-3): "We shouldn't do lock.
            If it's disabled then it's just lights out and flickering
            barely. If it's live, then it should be live barely."
            Removed the lock-icon overlay; the existing dim opacity +
            kansei-flicker variant on AppDisplay carries the disabled
            state. Next session: KANSEI to systematize a liveness model
            (live / barely-live / lights-out) across the board. */}
      </div>
    </button>
  );
});

ArcadeButton.displayName = "ArcadeButton";

interface AppDisplayProps {
  app: GameApp;
  isHovered: boolean;
}

export const AppDisplay = ({ app, isHovered }: AppDisplayProps) => {
  // KANSEI register routing: live cards run the "living" flicker (lit
  // baseline, brief struggle, hard recovery) + LIVING filter (mostly
  // colored, fighting to stay alive); locked cards run the "dying"
  // flicker (dim baseline, rare gasp toward life) + STRONG filter
  // (greyscale, wound down). Operator (session-4): "alive ones but
  // barely should be lit fully then barely; others are dead but trying
  // to breathe."
  const flickerClass = app.isLocked ? "kansei-flicker-c" : "kansei-flicker-living-c";
  const pokuFilter = app.isLocked
    ? HIBERNATION_POKU_FILTER_STRONG
    : HIBERNATION_POKU_FILTER_LIVING;

  // If there's a banner, display it full size with lower opacity
  if (app.banner) {
    return (
      <>
        <div className={`${flickerClass} absolute inset-0 transition-opacity duration-300`}>
          <Image
            src={app.banner}
            alt={app.title}
            fill
            sizes="(max-width: 768px) 92vw, (max-width: 1280px) 25vw, 25vw"
            className="object-cover transition-opacity duration-300"
            style={{
              opacity: app.isLocked ? 0.5 : isHovered ? 1 : 0.75,
              filter: pokuFilter,
            }}
          />
        </div>
        {/* Add an extra overlay to help blend the image */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 to-black/10" />
      </>
    );
  }

  return (
    <div className="absolute inset-0 flex items-center justify-center p-4 transition-opacity duration-300">
      {app.image ? (
        <div className={`${flickerClass} relative size-32`}>
          <Image
            src={app.image}
            alt={app.title}
            fill
            sizes="128px"
            className="object-contain transition-opacity duration-300"
            style={{
              opacity: app.isLocked ? 0.4 : isHovered ? 1 : 0.65,
              filter: pokuFilter,
            }}
          />
        </div>
      ) : (
        <div className={`${flickerClass} flex flex-col items-center gap-1`}>
          <h3
            className={`font-pressStart2P text-[10px] uppercase leading-none transition-opacity duration-300 ${app.isLocked ? "text-muted-foreground" : "text-zinc-500"}`}
            style={{
              opacity: app.isLocked ? 0.4 : isHovered ? 1 : 0.7,
            }}
          >
            {app.title.split(" ")[0]}
          </h3>
          {app.title.split(" ")[1] && (
            <span
              className={`font-pressStart2P text-[8px] uppercase ${app.isLocked ? "text-muted-foreground" : "text-muted-foreground"}`}
              style={{
                opacity: app.isLocked ? 0.4 : isHovered ? 0.8 : 0.5,
              }}
            >
              {app.title.split(" ")[1]}
            </span>
          )}
          {app.title.split(" ")[2] && (
            <span
              className={`font-pressStart2P text-[8px] uppercase ${app.isLocked ? "text-muted-foreground" : "text-muted-foreground"}`}
              style={{
                opacity: app.isLocked ? 0.4 : isHovered ? 0.8 : 0.5,
              }}
            >
              {app.title.split(" ")[2]}
            </span>
          )}
          <div className="mt-1">
            <div
              className={`font-pressStart2P text-[6px] uppercase tracking-widest text-muted-foreground`}
              style={{
                opacity: app.isLocked ? 0.4 : isHovered ? 0.7 : 0.4,
              }}
            >
              {app.subtitle}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
